import { FC } from 'react';
import { components } from '@types';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { userRoleMapper } from '@util/userRoleMapper.ts';
import { t } from 'i18next';
import { Delete } from '@mui/icons-material';
import { useComment } from '../hooks/useComment.ts';
import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import { MAXIMUM_COMMENT_LENGTH } from '@common/types/constants.ts';
import { ConfirmationModal, SaveChangesModal } from '@components';

interface CommentProps {
  details: components['schemas']['TaskCommentDTO'];
  isCommentOwner: boolean;
  task_id: string;
  commentToEditId: string;
  handleSetCommentToEdit: (id: string) => void;
  setNewCommentToEditId: (id: string) => void;
  handleRemoveCommentToEdit: () => void;
  setIsFormClean: (isDirty: boolean) => void;
  newCommentToEditId: string;
  isFormClean: boolean;
  triggerTabChange: boolean;
  setTriggerTabChange: (trigger: boolean) => void;
  setTriggerOpenCreateComment: (trigger: boolean) => void;
  handleOpenCreateComment: () => void;
  triggerOpenCreateComment: boolean;
  closeDrawer: () => void;
  triggerCloseDrawer: boolean;
  setTriggerCloseDrawer: (trigger: boolean) => void;
  isFormDisabled: boolean;
}

export const Comment: FC<CommentProps> = ({
  details,
  isCommentOwner,
  task_id,
  handleRemoveCommentToEdit,
  commentToEditId,
  handleSetCommentToEdit,
  newCommentToEditId,
  setIsFormClean,
  setNewCommentToEditId,
  isFormClean,
  triggerTabChange,
  setTriggerTabChange,
  triggerOpenCreateComment,
  setTriggerOpenCreateComment,
  handleOpenCreateComment,
  closeDrawer,
  setTriggerCloseDrawer,
  triggerCloseDrawer,
  isFormDisabled,
}) => {
  const {
    last_updated_at,
    created_by,
    comment,
    id: comment_id,
    edited,
  } = details;
  const {
    formMethods,
    onSubmit,
    isValid,
    isSubmitting,
    isDeleting,
    handleOpenConfirmDeleteModal,
    handleCloseConfirmDeleteModal,
    confirmDelete,
    isConfirmDeleteModalOpen,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    handleSetCommentToEditAction,
    handleCloseEditCommentAction,
  } = useComment({
    comment,
    comment_id,
    task_id,
    commentToEditId,
    handleRemoveCommentToEdit,
    newCommentToEditId,
    setIsFormClean,
    setNewCommentToEditId,
    handleSetCommentToEdit,
    isFormClean,
    triggerTabChange,
    setTriggerTabChange,
    triggerOpenCreateComment,
    setTriggerOpenCreateComment,
    handleOpenCreateComment,
    closeDrawer,
    setTriggerCloseDrawer,
    triggerCloseDrawer,
  });

  const { control } = formMethods;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor:
          created_by.role === 'provider'
            ? 'rgba(227, 240, 246, 1)'
            : 'rgba(250, 250, 250, 1)',
      }}
    >
      {commentToEditId === comment_id ? (
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: 'fit-content',
              position: 'relative',
              gap: '.5rem',
              marginTop: 8,
            }}
          >
            <Controller
              name='comment'
              control={control}
              disabled={isSubmitting}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextField
                    {...field}
                    sx={{ bgcolor: 'white' }}
                    type='text'
                    size='medium'
                    label={t('worklist.worklistItem.comments.comment')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={
                      error
                        ? error.message
                        : `${field.value ? field.value.length + `/${MAXIMUM_COMMENT_LENGTH}` : ''}`
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    slotProps={{
                      formHelperText: {
                        style: {
                          background: 'rgba(250, 250, 250, 1)',
                          textAlign: error ? 'left' : 'right',
                          margin: 0,
                          padding: '3px 14px 0',
                        },
                      },
                    }}
                  />

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '8px',
                    }}
                  >
                    <Button
                      variant='text'
                      sx={{
                        fontSize: '0,875rem',
                        fontWeight: 'medium',
                        lineHeight: '24px',
                      }}
                      disabled={isSubmitting}
                      onClick={handleCloseEditCommentAction}
                    >
                      {t('general.cancel')}
                    </Button>
                    <Button
                      type='submit'
                      variant='contained'
                      color='secondary'
                      sx={{
                        fontSize: '0,875rem',
                        fontWeight: 'medium',
                        lineHeight: '24px',
                        color: 'white',
                      }}
                      disabled={!isValid || !field.value || isSubmitting}
                    >
                      {t('general.save')}
                    </Button>
                  </Box>
                </>
              )}
            />
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isSubmitting}
            />
          </Form>
        </FormProvider>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant='subtitle2'>{created_by.name}</Typography>
            <Typography variant='caption' sx={{ color: 'text.disabled' }}>
              {`${dayjs(last_updated_at).format('MMM DD, YYYY - hh:mm A')} ${edited ? '(edited)' : ''}`}
            </Typography>
          </Box>
          <Typography variant='caption' sx={{ color: 'text.disabled' }}>
            {isCommentOwner ? 'You' : userRoleMapper(created_by.role)}
          </Typography>
          <Typography variant='body2' sx={{ my: 1, whiteSpace: 'pre-line' }}>
            {comment}
          </Typography>
          {isCommentOwner && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Button
                variant='text'
                sx={{ height: 30, minWidth: 39, paddingX: 0 }}
                onClick={handleSetCommentToEditAction}
                disabled={isDeleting || isFormDisabled}
              >
                {t('general.edit')}
              </Button>
              <IconButton
                sx={{ p: '3px', borderRadius: 0 }}
                disabled={isDeleting || isFormDisabled}
                onClick={handleOpenConfirmDeleteModal}
              >
                <Delete
                  color={isDeleting || isFormDisabled ? 'disabled' : 'error'}
                />
              </IconButton>
            </Box>
          )}
        </>
      )}
      <ConfirmationModal
        title={t('worklist.worklistItem.comments.deleteModal.title')}
        message={t('worklist.worklistItem.comments.deleteModal.message')}
        onConfirm={confirmDelete}
        onClose={handleCloseConfirmDeleteModal}
        isOpen={isConfirmDeleteModalOpen}
        isLoading={isDeleting}
        confirmText={t('general.delete')}
      />
    </Box>
  );
};

export default Comment;
